import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, Clock, MapPin, AlertCircle, TrendingUp,
  Calendar as CalIcon, Flame, Briefcase, Home, Mountain,
  CalendarX, CalendarCheck, Coffee, Activity, Zap, Hourglass, Users,
} from 'lucide-react';
import { formatDateToLocal } from '../lib/dateUtils';
import DataTabs from './data/DataTabs';
import { SnapshotTab, DailyTab } from '../pages/admin/AdminAttendancePage';
import EtimeofficeQueuePage from './EtimeofficeQueuePage';
import AttendanceHeatmap from './data/AttendanceHeatmap';
import AttendanceFilterBar, { ATTENDANCE_FILTER_DEFAULTS } from './reports/AttendanceFilterBar';
import { CalendarDays, BarChart3, Grid3x3, Table, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminReportsPage
 * -----------------
 * Reports console. Two sections per spec:
 *   1) Attendance & Presence — 16 metrics (live + Phase-2 pending stubs)
 *   2) Leave & Time Off       — 15 metrics (all live)
 *
 * All numbers come from a SINGLE `/api/admin/reports/comprehensive` call so
 * the per-employee view never has to stitch endpoints together.
 */
const AdminReportsPage = () => {
  const [activeTab, setActiveTab] = useState('attendance');

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-reports-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-600 mt-1">
          Organisation-wide insights and per-employee breakdowns. Every number
          is computed live — no cached snapshots.
        </p>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6" aria-label="Reports tabs">
          {[{ key: 'attendance', label: 'Attendance & Leave' }].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`reports-tab-${t.key}`}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'attendance' && <ReportsTab />}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ReportsTab = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [employees, setEmployees] = useState([]);
  // Single source of truth for ALL filters across all sub-tabs.
  const [filters, setFilters] = useState({
    ...ATTENDANCE_FILTER_DEFAULTS,
    fromDate: formatDateToLocal(firstOfMonth),
    toDate: formatDateToLocal(today),
  });
  const [activeSubtab, setActiveSubtab] = useState('overview');
  const [report, setReport] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);

  // Convenience destructure (read-only inside this component)
  const {
    fromDate, toDate, employeeId, department, designation, search,
    location: locationFilter, threshold: attendanceThreshold, sortBy,
  } = filters;

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/employees`, { headers: auth() });
        const raw = r.data?.employees || r.data || [];
        // Strict 'active' check — matches the dashboard's "Active Employees" KPI.
        const list = raw.filter((e) => (e.employment?.status || e.status || '').toLowerCase() === 'active');
        setEmployees(list);
        // Default to org-wide aggregate view ("All employees")
      } catch (e) {
        toast.error('Failed to load employees');
      }
    })();
  }, []);

  // Department list — canonical field is `employment.departments` (array).
  // Fall back to legacy `employment.department` (singular) for older records.
  const empDepartments = (e) => {
    const arr = e.employment?.departments;
    if (Array.isArray(arr) && arr.length) return arr;
    const single = e.employment?.department || e.department;
    return single ? [single] : [];
  };

  const departments = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => empDepartments(e).forEach((d) => d && set.add(d)));
    return Array.from(set).sort();
  }, [employees]);

  const designations = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      const d = e.employment?.designation;
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((e) => {
      if (department !== 'all' && !empDepartments(e).includes(department)) return false;
      if (designation !== 'all' && (e.employment?.designation || '') !== designation) return false;
      if (search) {
        const hay = ((e.personal?.full_name || '') + ' ' + (e.work_email || '') + ' ' + (e.employment?.employee_id || '')).toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [employees, department, designation, search]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/reports/attendance/all-employees`,
        { headers: auth(), params: { from: fromDate, to: toDate } }
      );
      setAllRows(r.data?.rows || []);
    } catch (e) {
      toast.error('Failed to load attendance overview');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const loadInventory = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/reports/leave-inventory`,
        { headers: auth(), params: { from: fromDate, to: toDate } }
      );
      setInventory(r.data);
    } catch (e) {
      toast.error('Failed to load leave inventory');
    }
  }, [fromDate, toDate]);

  const loadEmployee = useCallback(async () => {
    if (!employeeId || employeeId === 'all') {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/reports/comprehensive`,
        { headers: auth(), params: { employee_id: employeeId, from: fromDate, to: toDate } }
      );
      setReport(r.data);
    } catch (e) {
      toast.error('Failed to load comprehensive report');
    } finally {
      setLoading(false);
    }
  }, [employeeId, fromDate, toDate]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadEmployee(); }, [loadEmployee]);
  useEffect(() => { loadInventory(); }, [loadInventory]);

  // Apply ALL filters (department/designation/search/location/threshold) to the org table.
  const overviewFiltered = useMemo(() => {
    const validIds = new Set(filteredEmployees.map((e) => e._id));
    let rows = allRows.filter((r) => validIds.has(r.employee_id));

    // Location filter — non-trivial: a row matches if it has ANY time in that
    // bucket. "Pure office" employees won't match a "remote" filter.
    if (locationFilter !== 'all') {
      rows = rows.filter((r) => {
        const split = r.work_location_split || {};
        const key = locationFilter === 'remote' ? 'remote_days'
                  : locationFilter === 'onsite' ? 'onsite_days'
                  : 'office_days';
        return (split[key] || 0) > 0;
      });
    }

    if (attendanceThreshold === 'below_90') rows = rows.filter((r) => r.attendance_percentage < 90);
    if (attendanceThreshold === 'below_80') rows = rows.filter((r) => r.attendance_percentage < 80);
    if (attendanceThreshold === 'absent_only') rows = rows.filter((r) => r.approved_leave_days_total > 0);

    // Sorting
    rows = [...rows];
    if (sortBy === 'attendance_desc') rows.sort((a, b) => b.attendance_percentage - a.attendance_percentage);
    if (sortBy === 'attendance_asc') rows.sort((a, b) => a.attendance_percentage - b.attendance_percentage);
    if (sortBy === 'leave_desc') rows.sort((a, b) => b.approved_leave_days_total - a.approved_leave_days_total);
    if (sortBy === 'name_asc') rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return rows;
  }, [allRows, filteredEmployees, locationFilter, attendanceThreshold, sortBy]);

  // Aggregate KPIs for "All employees" view
  const aggregate = useMemo(() => {
    if (overviewFiltered.length === 0) return null;
    const sched = overviewFiltered.reduce((s, r) => s + (r.scheduled_working_days || 0), 0);
    const present = overviewFiltered.reduce((s, r) => s + (r.days_attended || 0), 0);
    const absent = overviewFiltered.reduce((s, r) => s + (r.approved_leave_days_total || 0), 0);
    const compoff = overviewFiltered.reduce((s, r) => s + (r.comp_off_balance || 0), 0);
    const officeDays = overviewFiltered.reduce((s, r) => s + ((r.work_location_split || {}).office_days || 0), 0);
    const remoteDays = overviewFiltered.reduce((s, r) => s + ((r.work_location_split || {}).remote_days || 0), 0);
    const onsiteDays = overviewFiltered.reduce((s, r) => s + ((r.work_location_split || {}).onsite_days || 0), 0);
    const avgRate = sched > 0 ? Math.round((present / sched) * 1000) / 10 : 0;
    return {
      employees: overviewFiltered.length,
      sched, present, absent, compoff,
      officeDays, remoteDays, onsiteDays,
      avgRate,
      perfectAttendance: overviewFiltered.filter((r) => r.attendance_percentage === 100).length,
      below90: overviewFiltered.filter((r) => r.attendance_percentage < 90).length,
    };
  }, [overviewFiltered]);

  return (
    <div className="space-y-6">
      <PhaseBanner />

      {/* Single, comprehensive filter bar for ALL sub-tabs */}
      <AttendanceFilterBar
        filters={filters}
        setFilters={setFilters}
        activeSubtab={activeSubtab}
        employees={filteredEmployees}
        departments={departments}
        designations={designations}
      />

      {/* Tabbed content — only the active tab renders, keeping DOM light. */}
      <DataTabs
        defaultTab="overview"
        urlParam="attendance_subtab"
        testid="attendance-subtabs"
        onTabChange={setActiveSubtab}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: BarChart3,
            render: () => (
              <>
                {employeeId === 'all' && aggregate && (
                  <OrgAggregateSection agg={aggregate} fromDate={fromDate} toDate={toDate} />
                )}
                {report && (
                  <>
                    <AttendanceSection attendance={report.attendance} employee={report.employee} />
                    <LeaveSection leave={report.leave} />
                  </>
                )}
                <OrgOverviewTable
                  rows={overviewFiltered}
                  loading={loading}
                  fromDate={fromDate}
                  toDate={toDate}
                  onSelect={(id) => setFilters((p) => ({ ...p, employeeId: id }))}
                />
              </>
            ),
          },
          {
            key: 'snapshot',
            label: 'Snapshot',
            icon: Table,
            render: () => (
              <SnapshotTab
                fromDate={fromDate}
                toDate={toDate}
                employeeId={employeeId !== 'all' ? employeeId : null}
                department={department !== 'all' ? department : null}
                onPickEmployee={(r) => setFilters((p) => ({ ...p, employeeId: r.employee_id }))}
              />
            ),
          },
          {
            key: 'daily',
            label: 'Daily',
            icon: CalendarDays,
            render: () => (
              <DailyTab
                fromDate={fromDate}
                toDate={toDate}
                employeeId={employeeId !== 'all' ? employeeId : null}
                department={department !== 'all' ? department : null}
                lateGt={filters.lateGt}
                otGt={filters.otGt}
                workLt={filters.workLt}
                status={filters.status}
                hideInternalFilters
              />
            ),
          },
          {
            key: 'heatmap',
            label: 'Heatmap',
            icon: Grid3x3,
            render: () => (
              <AttendanceHeatmap
                employeeId={employeeId !== 'all' ? employeeId : null}
                employeeName={employeeId !== 'all'
                  ? (filteredEmployees.find((e) => e._id === employeeId)?.personal?.full_name || 'Employee')
                  : null}
                initialYear={fromDate ? Number(fromDate.slice(0, 4)) : undefined}
              />
            ),
          },
          {
            key: 'inventory',
            label: 'Leave Inventory',
            icon: CalendarDays,
            render: () => (
              employeeId === 'all' && inventory ? (
                <LeaveInventoryTable
                  inventory={inventory}
                  filteredEmployeeIds={new Set(filteredEmployees.map((e) => e._id))}
                  onSelect={(id) => setFilters((p) => ({ ...p, employeeId: id }))}
                />
              ) : (
                <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center text-sm text-neutral-500">
                  Leave Inventory is shown across the whole org. Set the employee filter to <strong>All employees</strong> to view.
                </div>
              )
            ),
          },
          {
            key: 'queue',
            label: 'Approval Queue',
            icon: ClipboardList,
            render: () => <EtimeofficeQueuePage />,
          },
        ]}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

// ─── Leave inventory (taken / entitled per type per employee) ──────────────

const LeaveInventoryTable = ({ inventory, filteredEmployeeIds, onSelect }) => {
  const rows = inventory.rows.filter((r) => filteredEmployeeIds.has(r.employee_id));
  if (rows.length === 0) return null;

  // Org-level column totals for the footer
  const totals = inventory.columns.reduce((acc, col) => {
    acc[col.code] = rows.reduce((s, r) => s + (r.cells[col.code]?.taken || 0), 0);
    return acc;
  }, {});

  return (
    <Section
      title="Leave inventory"
      subtitle={
        <>Taken vs available per employee · <strong>{rows.length}</strong> employee
        {rows.length === 1 ? '' : 's'} · YTD math (period-independent)</>
      }
      headerRight={null}
    >
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-xs border-collapse" data-testid="leave-inventory-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[180px]">
                Employee
              </th>
              {inventory.columns.map((col) => (
                <th
                  key={col.code}
                  className="text-center px-2 py-2 min-w-[88px]"
                  title={
                    col.is_permission ? 'Permission (no fixed allocation)'
                    : col.is_unlimited ? 'No annual cap — subject to per-request approval'
                    : ''
                  }
                >
                  <div className="font-semibold text-gray-800">{col.code.toUpperCase()}</div>
                  <div className="text-[10px] text-gray-500 font-normal">{col.name}</div>
                  {col.is_permission && <div className="text-[9px] text-blue-600 uppercase">permission</div>}
                  {col.is_unlimited && <div className="text-[9px] text-purple-600 uppercase">no cap</div>}
                </th>
              ))}
              <th
                className="text-right px-3 py-2 min-w-[80px]"
                title="Sum of true leaves taken (OL, SL, PL, ML, etc.). Permissions (WFH, PROD) and uncapped types (COMP) are excluded."
              >
                <div className="font-semibold text-gray-800">Total</div>
                <div className="text-[10px] text-gray-500 font-normal">leaves only</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.employee_id}
                onClick={() => onSelect(r.employee_id)}
                className="border-b border-gray-100 hover:bg-blue-50/40 cursor-pointer"
                data-testid={`inventory-row-${r.employee_id}`}
              >
                <td className="px-3 py-2 sticky left-0 bg-white z-10 group-hover:bg-blue-50/40">
                  <div className="font-medium text-gray-900">{r.name}</div>
                  {r.department && (
                    <div className="text-[10px] text-gray-500">{r.department}</div>
                  )}
                </td>
                {inventory.columns.map((col) => {
                  const cell = r.cells[col.code] || { taken: 0, entitled: null, balance: null };
                  return <InventoryCell key={col.code} cell={cell} col={col} />;
                })}
                <td className="px-3 py-2 text-right font-medium text-gray-900">
                  {r.totals.taken}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-300">
              <td className="px-3 py-2 font-semibold sticky left-0 bg-gray-50 z-10">Org total</td>
              {inventory.columns.map((col) => (
                <td key={col.code} className="px-2 py-2 text-center font-semibold text-gray-900">
                  {Math.round(totals[col.code] * 10) / 10}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-gray-900">
                {Math.round(rows.reduce((s, r) => s + r.totals.taken, 0) * 10) / 10}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Click any employee row to open their full attendance + leave breakdown.
        Permissions (WFH, PROD) show only days used — no cap applies.
        Compensatory Leave (COMP) has no annual cap — each request is
        subject to HR approval with a mandatory <em>compensating-for</em> date.
      </p>
    </Section>
  );
};

const InventoryCell = ({ cell, col }) => {
  // Permissions (WFH/PROD): show only days used.
  // Unlimited (COMP): same treatment — no cap, no fraction — but tinted purple
  // so it's visually distinct from permissions.
  if (col.is_permission || col.is_unlimited) {
    const tint = col.is_unlimited ? 'text-purple-700' : 'text-blue-700';
    return (
      <td className="px-2 py-2 text-center" data-testid={`cell-${col.code}-uncapped`}>
        {cell.taken > 0 ? (
          <span className={`font-medium ${tint}`}>{cell.taken}d</span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
    );
  }
  const taken = cell.taken || 0;
  const entitled = cell.entitled || 0;
  const balance = cell.balance ?? Math.max(entitled - taken, 0);
  const pct = entitled > 0 ? (taken / entitled) * 100 : 0;
  // Color the cell by usage intensity
  const intensity =
    pct === 0 ? 'text-gray-400'
    : pct < 50 ? 'text-green-700'
    : pct < 80 ? 'text-amber-700'
    : 'text-red-700';
  return (
    <td className="px-2 py-2 text-center">
      <div className={`font-medium ${intensity}`}>
        {taken}<span className="text-gray-400"> / </span>{entitled}
      </div>
      <div className="text-[10px] text-gray-500">
        {balance > 0 ? `${balance} left` : (entitled > 0 ? 'used up' : '—')}
      </div>
    </td>
  );
};

// ─── All-employees aggregate ────────────────────────────────────────────────

const OrgAggregateSection = ({ agg, fromDate, toDate }) => (
  <Section
    title="Organisation Snapshot"
    subtitle={`${agg.employees} employee${agg.employees === 1 ? '' : 's'} · ${fromDate} → ${toDate}`}
    headerRight={
      <div className="text-right">
        <div className="text-3xl font-semibold text-gray-900" data-testid="org-avg-attendance">
          {agg.avgRate}%
        </div>
        <div className="text-xs text-gray-500">Average attendance</div>
      </div>
    }
  >
    <KpiGrid testid="org-aggregate-kpis">
      <Kpi icon={Users} color="blue" label="Employees in view" value={agg.employees} />
      <Kpi icon={CheckCircle} color="green" label="Total Present-Days" value={agg.present.toFixed(1)} />
      <Kpi icon={CalendarX} color="red" label="Total Leave-Days" value={agg.absent.toFixed(1)} />
      <Kpi icon={CalIcon} color="gray" label="Scheduled Working Days" value={agg.sched.toFixed(1)} />
      <Kpi icon={Flame} color="orange" label="Perfect Attendance (100%)" value={agg.perfectAttendance} />
      <Kpi icon={AlertCircle} color="amber" label="Below 90%" value={agg.below90} />
      <Kpi icon={Briefcase} color="blue" label="Office Days" value={agg.officeDays.toFixed(1)} />
      <Kpi icon={Home} color="teal" label="Remote (WFH) Days" value={agg.remoteDays.toFixed(1)} />
      <Kpi icon={Mountain} color="amber" label="On-Site Days" value={agg.onsiteDays.toFixed(1)} />
      <Kpi icon={CalendarCheck} color="purple" label="Comp-Off Balance (sum)" value={agg.compoff} />
    </KpiGrid>
    <p className="text-xs text-gray-500 mt-4">
      Pick an individual from the View dropdown above to drill into their full
      attendance + leave report.
    </p>
  </Section>
);

// ─────────────────────────────────────────────────────────────────────────────

const PhaseBanner = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 items-start" data-testid="phase1-banner">
    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
    <div className="text-sm text-blue-900">
      <p className="font-medium mb-1">Phase 1 — Leave-derived attendance</p>
      <p>
        Until the attendance-machine integration is live, days attended are
        derived from leave records. Anyone not on an approved leave on a
        scheduled working day is counted as present. Metrics that need
        time-of-day data (late arrivals, hours logged, OT, break duration, etc.)
        show <strong>Pending integration</strong> and activate automatically once
        the machine API ships.
      </p>
    </div>
  </div>
);

// `Filter` and `QuickRanges` helpers were removed when the unified
// `AttendanceFilterBar` took over filter UI duties. The filter bar is the
// single source of truth for filters across all attendance sub-tabs.

// ─── Section 1: Attendance ──────────────────────────────────────────────────

const AttendanceSection = ({ attendance, employee }) => {
  const a = attendance;
  const loc = a.work_location_split || {};
  const breakdown = a.approved_leaves_breakdown || {};
  const waBreakdown = a.work_attendance_breakdown || {};
  // Effective period the backend used (may be clamped to the employee's
  // joining / exit date if they weren't active for the whole requested range).
  const eff = a.period || {};

  return (
    <Section
      title="Attendance & Presence"
      subtitle={
        <>
          {employee.name}{employee.department ? ' · ' + employee.department : ''}
          {eff.from && eff.to && (
            <span className="ml-2 text-xs text-gray-400" data-testid="attendance-effective-period">
              · effective {eff.from} → {eff.to}
            </span>
          )}
        </>
      }
      headerRight={
        <div className="text-right">
          <div className="text-3xl font-semibold text-gray-900" data-testid="attendance-rate">
            {a.attendance_rate_percent}%
          </div>
          <div className="text-xs text-gray-500">Attendance rate</div>
        </div>
      }
    >
      {/* Live KPIs */}
      <KpiGrid testid="attendance-live-kpis">
        <Kpi icon={CheckCircle} color="green" label="Days Present" value={a.days_present} />
        <Kpi icon={CalendarX} color="red" label="Days Absent" value={a.days_absent} />
        <Kpi icon={CalIcon} color="blue" label="Scheduled Working Days" value={a.scheduled_working_days} />
        <Kpi icon={TrendingUp} color="gray" label="Absenteeism Rate" value={`${a.absenteeism_rate_percent}%`} />
        <Kpi icon={AlertCircle} color="gray" label="Unplanned Absences" value={a.unplanned_absence_count} note="Phase 2 source" />
        <Kpi icon={Flame} color="orange" label="Present-Day Streak" value={a.present_day_streak} />
        <Kpi icon={CalendarCheck} color="purple" label="Weekend / Holiday Days Worked" value={a.weekend_holiday_days_worked} />
        <Kpi icon={Briefcase} color="blue" label="Office Days" value={loc.office_days ?? 0} />
        <Kpi icon={Home} color="teal" label="Remote (WFH) Days" value={loc.remote_days ?? 0} />
        <Kpi icon={Mountain} color="amber" label="On-Site / Shoot Days" value={loc.onsite_days ?? 0} />
      </KpiGrid>

      {Object.keys(breakdown).length > 0 && (
        <Pills
          label="Approved Leave Breakdown (absence)"
          color="amber"
          items={breakdown}
          testid="attendance-leave-breakdown"
        />
      )}
      {Object.keys(waBreakdown).length > 0 && (
        <Pills
          label="On-Duty Work Breakdown (counted as attended)"
          color="blue"
          items={waBreakdown}
          testid="attendance-work-breakdown"
        />
      )}

      {/* Pending Phase 2 */}
      <div className="border-t border-gray-200 pt-5 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-medium text-gray-700">Pending Attendance-Machine Integration</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="attendance-pending-machine">
          {Object.keys(a.machine_metrics_pending || {}).map((k) => (
            <PendingMetric key={k} label={prettyMachineLabel(k)} icon={pendingIcon(k)} />
          ))}
        </div>
      </div>
    </Section>
  );
};

// ─── Section 2: Leave ───────────────────────────────────────────────────────

const LeaveSection = ({ leave }) => {
  const l = leave;
  const types = Object.entries(l.balance_by_type || {});
  return (
    <Section
      title="Leave & Time Off"
      subtitle="Year-to-date entitlements & utilisation"
      headerRight={
        <div className="text-right">
          <div className="text-3xl font-semibold text-gray-900" data-testid="leave-utilization">
            {l.leave_utilization_percent}%
          </div>
          <div className="text-xs text-gray-500">Leave utilisation</div>
        </div>
      }
    >
      {/* Live KPI cards */}
      <KpiGrid testid="leave-kpis">
        <Kpi icon={CalIcon} color="amber" label="Total Days Taken (period)" value={l.taken_total_days} />
        <Kpi icon={Hourglass} color="blue" label="Pending Requests" value={l.pending_requests_count} />
        <Kpi icon={Activity} color="gray" label="Half-Day Count" value={l.half_day_count} />
        <Kpi icon={Zap} color="red" label="Last-Minute Leave Count" value={l.last_minute_leave_count} />
        <Kpi icon={CalIcon} color="blue" label="Avg Lead Time (days)" value={l.average_lead_time_days} />
        <Kpi icon={Coffee} color="gray" label="LWP Days" value={l.leave_without_pay_days} />
        <Kpi icon={CalendarCheck} color="purple" label="Optional Holidays Availed" value={l.optional_holidays_availed} />
        <Kpi icon={CalendarCheck} color="teal" label="Maternity / Paternity / Bereavement" value={l.maternity_paternity_bereavement_days} />
        <Kpi icon={TrendingUp} color="green" label="Comp-Offs Earned" value={l.comp_offs_earned} />
        <Kpi icon={TrendingUp} color="amber" label="Comp-Offs Redeemed" value={l.comp_offs_redeemed} />
      </KpiGrid>

      {/* Balance table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden mt-5">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">Balance by leave type (YTD)</h3>
        </div>
        <table className="w-full text-sm" data-testid="leave-balance-table">
          <thead className="bg-white text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-right">Entitled</th>
              <th className="px-4 py-2 text-right">Taken (YTD)</th>
              <th className="px-4 py-2 text-right">Balance</th>
              <th className="px-4 py-2 text-right">Carry-Forward Cap</th>
              <th className="px-4 py-2 text-right">Notice Required</th>
              <th className="px-4 py-2 text-center">Auto-Approved</th>
            </tr>
          </thead>
          <tbody>
            {types.map(([code, v]) => (
              <tr key={code} className="border-t border-gray-100" data-testid={`leave-row-${code}`}>
                <td className="px-4 py-2">
                  <span className="font-medium text-gray-900">{v.name}</span>
                  <span className="ml-2 text-xs text-gray-400 uppercase">{code}</span>
                  {v.is_permission && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                      Permission
                    </span>
                  )}
                  {v.is_unlimited && (
                    <span
                      className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded"
                      title="No annual cap — subject to per-request approval"
                    >
                      No cap
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">{v.entitled === null ? '∞' : v.entitled}</td>
                <td className="px-4 py-2 text-right">{v.taken}</td>
                <td className="px-4 py-2 text-right">
                  {v.balance === null ? (
                    <span className="text-blue-700">∞</span>
                  ) : (
                    <span className={`font-medium ${v.balance > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                      {v.balance}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-gray-600">
                  {v.carry_forward_allowed ? v.carry_forward_cap : '—'}
                </td>
                <td className="px-4 py-2 text-right text-gray-600">
                  {v.advance_notice_days > 0 ? `${v.advance_notice_days}d` : '—'}
                </td>
                <td className="px-4 py-2 text-center">
                  {v.auto_approved ? '✓' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Taken by type (period) */}
      {Object.keys(l.taken_by_type || {}).length > 0 && (
        <Pills
          label="Leaves taken by type (filtered period)"
          color="amber"
          items={l.taken_by_type}
          testid="leave-taken-by-type"
        />
      )}

      {/* Sick pattern flag */}
      <div
        className={`mt-5 border rounded-lg p-4 ${
          l.sick_leave_pattern_flag ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
        }`}
        data-testid="sick-pattern-card"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-medium ${l.sick_leave_pattern_flag ? 'text-red-800' : 'text-gray-700'}`}>
              Sick-leave clustering
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Approved SL requests starting Mon or ending Fri — flagged at ≥{l.sick_leave_pattern_details?.threshold}.
            </p>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-semibold ${l.sick_leave_pattern_flag ? 'text-red-700' : 'text-gray-900'}`}>
              {l.sick_leave_pattern_details?.weekend_adjacent_total ?? 0}
            </div>
            <div className="text-xs text-gray-500">weekend-adjacent</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3 text-xs text-gray-600">
          <div>Started Monday: <span className="font-medium text-gray-900">{l.sick_leave_pattern_details?.mondays ?? 0}</span></div>
          <div>Ended Friday: <span className="font-medium text-gray-900">{l.sick_leave_pattern_details?.fridays ?? 0}</span></div>
        </div>
      </div>
    </Section>
  );
};

// ─── Org overview table ─────────────────────────────────────────────────────

const OrgOverviewTable = ({ rows, loading, fromDate, toDate, onSelect }) => (
  <div className="bg-white border border-gray-200 rounded-lg">
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
      <h2 className="font-medium text-gray-900">All employees · {fromDate} → {toDate}</h2>
      <span className="text-xs text-gray-500">
        {loading ? 'Loading…' : `${rows.length} employee(s)`}
      </span>
    </div>
    <table className="w-full text-sm" data-testid="attendance-org-table">
      <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
        <tr>
          <th className="px-4 py-2 text-left">Employee</th>
          <th className="px-4 py-2 text-left">Department</th>
          <th className="px-4 py-2 text-right">Scheduled</th>
          <th className="px-4 py-2 text-right">Present</th>
          <th className="px-4 py-2 text-right">Leave</th>
          <th className="px-4 py-2 text-right">Attendance %</th>
          <th className="px-4 py-2 text-right">Comp-off</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.employee_id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelect(r.employee_id)}
              data-testid={`org-row-${r.employee_id}`}>
            <td className="px-4 py-2">
              {r.name}
              {r.effective_from && r.effective_from !== fromDate && (
                <span
                  className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-800 rounded border border-amber-200"
                  title={`Active in period since ${r.effective_from}`}
                  data-testid={`org-row-clamped-${r.employee_id}`}
                >
                  since {r.effective_from}
                </span>
              )}
            </td>
            <td className="px-4 py-2 text-gray-600">{r.department || '—'}</td>
            <td className="px-4 py-2 text-right">{r.scheduled_working_days}</td>
            <td className="px-4 py-2 text-right">{r.days_attended}</td>
            <td className="px-4 py-2 text-right">{r.approved_leave_days_total}</td>
            <td className="px-4 py-2 text-right">
              <span className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${
                r.attendance_percentage >= 95 ? 'bg-green-100 text-green-800' :
                r.attendance_percentage >= 80 ? 'bg-amber-100 text-amber-800' :
                'bg-red-100 text-red-800'
              }`}>
                {r.attendance_percentage}%
              </span>
            </td>
            <td className="px-4 py-2 text-right">{r.comp_off_balance}</td>
          </tr>
        ))}
        {!loading && rows.length === 0 && (
          <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-500">No employees in this period.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

// ─── Generic UI primitives ──────────────────────────────────────────────────

const Section = ({ title, subtitle, headerRight, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6">
    <div className="flex items-center justify-between mb-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
      {headerRight}
    </div>
    {children}
  </div>
);

const KpiGrid = ({ children, testid }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid={testid}>
    {children}
  </div>
);

const Kpi = ({ icon: Icon, label, value, color, note }) => {
  const colors = {
    green: 'bg-green-50 text-green-700 border-green-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    teal: 'bg-teal-50 text-teal-700 border-teal-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[color] || colors.gray}`}>
      <div className="flex items-center gap-2 text-xs font-medium">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-semibold mt-1">{value}</div>
      {note && <div className="text-[10px] text-gray-500 mt-0.5">{note}</div>}
    </div>
  );
};

const Pills = ({ label, items, color, testid }) => {
  const palette = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };
  return (
    <div className="mt-4" data-testid={testid}>
      <h3 className="text-sm font-medium text-gray-700 mb-2">{label}</h3>
      <div className="flex flex-wrap gap-2">
        {Object.entries(items).map(([k, v]) => (
          <span key={k} className={`px-3 py-1 border rounded-full text-xs ${palette[color] || palette.amber}`}>
            <strong>{k.toUpperCase()}</strong>: {v} day(s)
          </span>
        ))}
      </div>
    </div>
  );
};

const PendingMetric = ({ label, icon: Icon }) => (
  <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50">
    <div className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </div>
    <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1">
      <Clock className="w-3 h-3" /> Pending integration
    </div>
  </div>
);

const prettyMachineLabel = (k) => ({
  late_arrivals_count: 'Late Arrivals',
  late_arrival_frequency_percent: 'Late Arrival Frequency',
  early_departures_count: 'Early Departures',
  average_check_in_time: 'Avg Check-in Time',
  average_check_out_time: 'Avg Check-out Time',
  total_hours_logged: 'Total Hours Logged',
  average_daily_hours: 'Avg Daily Hours',
  overtime_hours: 'Overtime Hours',
  undertime_hours: 'Undertime Hours',
  punctuality_score: 'Punctuality Score',
  average_break_duration_minutes: 'Avg Break Duration',
}[k] || k);

const pendingIcon = (k) => {
  if (k.includes('check_in') || k.includes('check_out') || k.includes('hours')) return Clock;
  if (k.includes('break')) return Coffee;
  if (k.includes('punctuality')) return Activity;
  return AlertCircle;
};

export default AdminReportsPage;
